import { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useLatest, usePrevious } from 'react-use';
import DateField from '../forms/date-field';
import TimeField from '../forms/time-field';
import TextField from '../forms/text-field';
import { momentParse } from '../../utils/time';

const useStyles = makeStyles((theme) => ({
  dateAndTime: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
    '& *.MuiFormControl-root': {
      margin: '0 !important',
    },
  },
  field: {
    margin: 0,
  },
  button: {
    margin: theme.spacing(0, 0, 1, 0),
  },
  buttonIcon: {
    margin: theme.spacing(0, 0.5, 0, 0),
  },
}));

const EventFormTime = ({ disabled, forceTouched }) => {
  const classes = useStyles();
  const {
    setFieldValue,
    values: { startDate, startTime, endDate, endTime },
  } = useFormikContext();
  const [open, setOpen] = useState(
    endDate && endTime && startDate !== endDate && startTime !== endTime
  );
  const today = useMemo(() => momentParse(), []);

  const toggleOpen = useCallback(() => {
    if (open) {
      setFieldValue('endDate', null);
      setFieldValue('endTime', null);
    } else {
      const momentStartDate = moment(startDate);
      const momentStartTime = moment(startTime);
      const endDate = momentStartDate.isValid() ? momentStartDate : null;
      const endTime = momentStartTime.isValid() ? momentStartTime : null;
      setFieldValue('endDate', endDate);
      setFieldValue('endTime', endTime);
    }
    setOpen(!open);
  }, [open, startDate, startTime]);

  const handleIsValidDate = useCallback((currentDate) => {
    return currentDate.isSameOrAfter(today, 'day');
  }, [today]);

  // make sure start and end dates are in order
  const latestStartDate = useLatest(startDate);
  const previousStartDate = usePrevious(startDate);
  const latestPreviousStartDate = useLatest(previousStartDate);
  const latestEndDate = useLatest(endDate);
  useEffect(() => {
    if (!startDate || !latestEndDate.current) {
      return;
    }
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(latestEndDate.current);
    if (!momentStartDate.isValid() || !momentEndDate.isValid()) {
      return;
    }
    if (momentEndDate.isBefore(momentStartDate, 'day')) {
      const previousMomentStartDate = moment(latestPreviousStartDate.current);
      const previousDiff = momentEndDate.diff(previousMomentStartDate);
      setFieldValue('endDate', moment(momentStartDate).add(previousDiff));
    }
  }, [startDate]);
  useEffect(() => {
    if (!latestStartDate.current || !endDate) {
      return;
    }
    const momentStartDate = moment(latestStartDate.current);
    const momentEndDate = moment(endDate);
    if (!momentStartDate.isValid() || !momentEndDate.isValid()) {
      return;
    }
    if (momentEndDate.isBefore(momentStartDate, 'day')) {
      setFieldValue('startDate', momentEndDate);
    }
  }, [endDate]);

  return (
    <Fragment>
      <Typography variant="h2">Date and Time</Typography>
      <div className={classes.dateAndTime}>
        <DateField
          name="startDate"
          label="Start Date"
          labelWidth={73}
          disabled={disabled}
          touched={forceTouched}
          className={classes.field}
          isValidDate={handleIsValidDate}
        />
        <TimeField
          name="startTime"
          label="Start Time"
          labelWidth={76}
          disabled={disabled}
          touched={forceTouched}
          className={classes.field}
        />
      </div>
      <TextField
        name="startAtDetails"
        label="Start Time Details (optional)"
        helperText="Example: Meet at 6, leave at 6:30"
        disabled={disabled}
        touched={forceTouched}
      />
      {open && (
        <div className={classes.dateAndTime}>
          <DateField
            name="endDate"
            label="End Date (optional)"
            labelWidth={140}
            disabled={disabled}
            touched={forceTouched}
            isValidDate={handleIsValidDate}
          />
          <TimeField
            name="endTime"
            label="End Time (optional)"
            labelWidth={143}
            disabled={disabled}
            touched={forceTouched}
          />
        </div>
      )}
      <Button
        variant="outlined"
        onClick={toggleOpen}
        className={classes.button}
      >
        {open
          ? <RemoveIcon className={classes.buttonIcon} />
          : <AddIcon className={classes.buttonIcon} />}
        End Date and Time (optional)
      </Button>
    </Fragment>
  );
};

EventFormTime.propTypes = {
  disabled: PropTypes.bool.isRequired,
  forceTouched: PropTypes.bool.isRequired,
};

export default EventFormTime;
